/**
 * @file FooterAutoList.tsx
 * @description Содержимое колонки-автосписка «Категории каталога» в
 * «Меню → Подвал» (phase19.16, приёмка Р2а; SB113; uiux-footer-columns §4.6,
 * §5): категории собираются сами — список серым, без перетаскивания;
 * «Сколько показывать» (то же поле `autoLimit`, что в окне пункта) и мягкая
 * подсказка, если категорий в подвале больше 8.
 */

import { SITE_LIMITS } from "@chaiberi/shared";

import { Label } from "../../ui/label";
import { SelectField } from "../../ui/SelectField";
import { FOOTER_AUTO_SOFT_MAX } from "../../../lib/site-view/footer-columns";

interface Props {
  id: string;
  /** null — сколько есть, но не больше лимита. */
  autoLimit: number | null;
  /** Категории автосписка по порядку; null — список не загружен. */
  categories: readonly string[] | null;
  onLimit: (limit: number | null) => void;
}

const LIMIT_OPTIONS = [
  { value: "", label: `Все (до ${SITE_LIMITS.catalogCategoriesAutoLimit})` },
  ...Array.from({ length: SITE_LIMITS.catalogCategoriesAutoLimit }, (_, i) => ({ value: String(i + 1), label: String(i + 1) })),
];

export function FooterAutoList({ id, autoLimit, categories, onLimit }: Props) {
  const limit = autoLimit ?? SITE_LIMITS.catalogCategoriesAutoLimit;
  const shown = categories ? categories.slice(0, limit) : null;
  return (
    <div className="flex flex-col gap-2">
      <span className="type-body text-on-surface">Категории каталога — сами</span>
      {shown === null ? (
        <span className="type-caption text-on-surface-variant">Список соберётся из опубликованных категорий с товарами.</span>
      ) : shown.length === 0 ? (
        <span className="type-caption text-on-surface-variant">Сейчас категорий с товарами нет — на сайте колонки не будет.</span>
      ) : (
        <ul aria-label="Категории в подвале" className="type-body flex flex-col gap-1 pl-3 text-on-surface-variant">
          {/* Названия категорий могут совпадать — ключ с позицией (QA Р3а D10). */}
          {shown.map((name, i) => (
            <li key={`${i}:${name}`} className="truncate" title={name}>
              {name}
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Label htmlFor={id}>Сколько показывать</Label>
        <SelectField
          id={id}
          ariaLabel="Сколько категорий показывать"
          className="w-36"
          value={autoLimit === null ? "" : String(autoLimit)}
          placeholder={LIMIT_OPTIONS[0]!.label}
          options={LIMIT_OPTIONS}
          onChange={(value) => onLimit(value === "" ? null : Number(value))}
        />
        {shown ? <span className="type-caption text-on-surface-variant tabular-nums">сейчас {shown.length}</span> : null}
      </div>
      {shown && shown.length > FOOTER_AUTO_SOFT_MAX ? (
        <p className="type-caption text-on-surface-variant">В подвале лучше до 8 категорий — остальные покупатель найдёт в каталоге.</p>
      ) : null}
    </div>
  );
}
